import classNames from "classnames";
import { ModalBody } from "reactstrap";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button } from "../Button";
import CustomizeCookiesSelection from "../CustomizeCookiesSelection";

import useCookies from "@hooks/useCookies";
import { useService } from "@/base/hooks/useService";

import { StorageService } from "@services/StorageService";

import { ReactComponent as CookieImage } from "@assets/carbon_cookie.svg";

import { differenceInMonths } from "date-fns";

import routes from "@const/routes";
import { localstorageKeys } from "@const/store";
import { MAX_EXPIRATION_DURATION_IN_MONTHS } from "@const/cookies";

import styles from "./index.module.scss";

const CookiesBanner = () => {
  const { t } = useTranslation("buttons");
  const { t: t1 } = useTranslation("cookies-banner");
  const storageService = useService(StorageService);

  const [isCookiesBannerOpen, setIsCookiesBannerOpen] = useState(false);

  const toggle = () => {
    setIsCookiesBannerOpen((prevState) => !prevState);
  };

  const onNecessaryCookiesChange = () => {
    const necessaryCookies = storageService.get(
      localstorageKeys.necessaryCookies,
      false,
    );
    const difference = differenceInMonths(
      +new Date(necessaryCookies),
      +new Date(),
    );

    if (necessaryCookies && difference >= MAX_EXPIRATION_DURATION_IN_MONTHS) {
      storageService.remove(localstorageKeys.necessaryCookies);
      toggle();
    } else if (!necessaryCookies) {
      toggle();
    }
  };

  useEffect(onNecessaryCookiesChange, []);

  useEffect(() => {
    storageService.onChange(
      localstorageKeys.necessaryCookies,
      onNecessaryCookiesChange,
    );
  }, [onNecessaryCookiesChange]);

  const { setFunctionalCookies, setMarketingCookies, setNecessaryCookies } =
    useCookies();

  const [
    isCustomizeCookiesSelectionModalOpen,
    setCustomizeCookiesSelectionModalOpen,
  ] = useState(false);

  const toggleCustomizeCookiesSelectionModal = () => {
    setCustomizeCookiesSelectionModalOpen((prevState) => !prevState);
  };

  const buttonsConfig = [
    {
      id: "buttons-config-1",
      textKey: "REJECT_NON_ESSENTIAL",
      color: "secondary",
      onClick: () => {
        setIsCookiesBannerOpen(false);
        setNecessaryCookies();
      },
    },
    {
      id: "buttons-config-2",
      textKey: "CUSTOMIZE",
      color: "secondary",
      onClick: () => {
        setIsCookiesBannerOpen(false);
        toggleCustomizeCookiesSelectionModal();
      },
    },
    {
      id: "buttons-config-3",
      textKey: "ACCEPT_ALL",
      color: "primary",
      onClick: () => {
        setIsCookiesBannerOpen(false);
        setFunctionalCookies(new Date());
        setMarketingCookies(new Date());
        setNecessaryCookies();
      },
    },
  ];

  const goToCookiePolicy = () => {
    window.open(routes.COOKIE_POLICY(), "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <CustomizeCookiesSelection
        toggle={toggleCustomizeCookiesSelectionModal}
        isOpen={isCustomizeCookiesSelectionModalOpen}
      />
      {isCookiesBannerOpen && (
        <div className={styles.modal}>
          <ModalBody className="p-0 overflow-hidden">
            <div className={styles.header}>
              <CookieImage />
              <h3 className="h3 text-primary text-center">{t1("TITLE")}</h3>
            </div>

            <span className="text-primary text-body-2">
              <Trans
                t={t1}
                components={{
                  cookiePolicy: (
                    <span
                      className="text-orange cursor-pointer"
                      onClick={goToCookiePolicy}
                    >
                      {t1("COOKIE_POLICY")}
                    </span>
                  ),
                }}
                i18nKey="DESCRIPTION"
              />
            </span>
            <div
              className={classNames(
                "d-flex gap-3",
                styles.footerButtonsContainer,
              )}
            >
              {buttonsConfig.map((config) => (
                <Button {...config} key={config.id} className={styles.btn}>
                  {t(config.textKey)}
                </Button>
              ))}
            </div>
          </ModalBody>
        </div>
      )}
    </>
  );
};

export default CookiesBanner;
